import type {
  NflBoxGameState,
  NflDrive,
  NflEvent,
  NflGameDataResponse,
  NflPlayByPlayResponse,
  NflPlayEvent,
  NflScoringEvent,
  NflStatKey,
  NflTeamGameModel,
} from '@statmuse/core/gamera'
import { last } from 'lodash-es'
import { map, atom, computed } from 'nanostores'
import type {
  NflPlayersMediaQuery,
  CfbPlayersMediaQuery,
} from '@statmuse/core/../graphql/graphql'

export const gameScore = map({ home: 0, away: 0 })

export const playerStats = map<{
  away?: NonNullable<NflTeamGameModel<NflStatKey>['stats']>['player']
  home?: NonNullable<NflTeamGameModel<NflStatKey>['stats']>['player']
}>({})

export const teamStats = map<{
  away?: NonNullable<NflTeamGameModel<NflStatKey>['stats']>['team']
  home?: NonNullable<NflTeamGameModel<NflStatKey>['stats']>['team']
}>({})

export const timeouts = map<{ away?: number; home?: number }>({
  away: 0,
  home: 0,
})

export const drives = atom<NflDrive[]>([])

export const events = atom<NflEvent[]>([])

export const scoringEvents = atom<NflScoringEvent[]>([])

export const lineScore = atom<{
  away?: NflGameDataResponse<NflStatKey>['awayTeam']['lineScore']
  home?: NflGameDataResponse<NflStatKey>['awayTeam']['lineScore']
}>({})

export const players = map<
  Record<
    string,
    | NonNullable<NflPlayersMediaQuery['nfl']['players']>[number]
    | NonNullable<CfbPlayersMediaQuery['cfb']['players']>[number]
    | undefined
  >
>({})

export const currentDrive = computed([drives, events], ($drives, $events) => {
  const lastDrive = last($drives)

  const plays = $events
    .filter((e) => e.driveSequence === lastDrive?.driveSequence)
    .filter((e) => e.type === 'play')
  const currentPlay = last(plays)

  const scrimmagePlays = plays.filter((p) =>
    ['pass', 'rush', 'play', 'penalty'].includes(p.playType),
  )
  const currentScrimmagePlay = last(scrimmagePlays)
  const firstScrimmagePlay = scrimmagePlays[0] as NflPlayEvent | undefined

  return {
    firstScrimmagePlay,
    currentScrimmagePlay,
    quarter: currentPlay?.period ?? 1,
    possessionTeamId: currentPlay?.endSituation.possessionTeamId,
    duration: lastDrive?.duration,
    gainYards: lastDrive?.gainYards,
    currentPlay,
    numberPlays: lastDrive?.playCount ?? 0,
    description: last(plays)?.description,
    startOpponentSide:
      firstScrimmagePlay?.startSituation.location.teamId !==
      firstScrimmagePlay?.startSituation.possessionTeamId,
  }
})

export const quarterInfo = computed([events], ($events) => {
  const lastEvent = last($events)
  return {
    isHalftime:
      lastEvent?.type === 'nonPlay' &&
      lastEvent.nonPlayType === 'periodEnd' &&
      lastEvent.period === 2,
    isGameOver:
      lastEvent?.type === 'nonPlay' && lastEvent.nonPlayType === 'gameOver',
    isEndOfQuarter:
      lastEvent?.type === 'nonPlay' && lastEvent.nonPlayType === 'periodEnd',
  }
})

export const selectedId = atom<
  { id: number; alignment: 'home' | 'away' } | undefined
>()

export const selectedPlayer = computed(
  [selectedId, players, playerStats],
  ($selectedId, $players, $stats) => {
    if (!$selectedId) return undefined
    const player = $players[$selectedId.id + '']
    const stats =
      $stats[$selectedId.alignment]?.statsLookup['*:0:p-' + $selectedId.id]

    return {
      player,
      stats,
      alignment: $selectedId.alignment,
    }
  },
)

export const init = (props: {
  gameData: NflGameDataResponse<NflStatKey>
  playByPlay?: NflPlayByPlayResponse
  players: Record<
    string,
    | NonNullable<NflPlayersMediaQuery['nfl']['players']>[number]
    | NonNullable<CfbPlayersMediaQuery['cfb']['players']>[number]
    | undefined
  >
}) => {
  gameScore.set({
    away: props.gameData.awayTeam.score,
    home: props.gameData.homeTeam.score,
  })
  playerStats.set({
    away: props.gameData.awayTeam.stats?.player,
    home: props.gameData.homeTeam.stats?.player,
  })
  teamStats.set({
    away: props.gameData.awayTeam.stats?.team,
    home: props.gameData.homeTeam.stats?.team,
  })
  timeouts.set({
    away: props.gameData.awayTeam.usedTimeouts,
    home: props.gameData.homeTeam.usedTimeouts,
  })
  drives.set(props.playByPlay?.drives ?? [])
  events.set(props.playByPlay?.events ?? [])
  scoringEvents.set(props.playByPlay?.scoringEvents ?? [])
  lineScore.set({
    away: props.gameData.awayTeam.lineScore,
    home: props.gameData.homeTeam.lineScore,
  })
  players.set(props.players)
}

export const update = (data: NflBoxGameState) => {
  gameScore.set(data.gameScore)
  playerStats.set(data.playerStats)
  teamStats.set(data.teamStats)
  scoringEvents.set(data.scoringEvents)
  lineScore.set(data.lineScore)
  timeouts.set(data.usedTimeouts)
  players.set({
    ...players.get(),
    ...data.players,
  })

  const lastDrive = data.lastDrive
  if (lastDrive) {
    drives.set([
      ...drives
        .get()
        .filter((d) => d.driveSequence !== lastDrive.driveSequence),
      lastDrive,
    ])
  }

  events.set([
    ...events.get().filter((e) => e.driveSequence !== lastDrive?.driveSequence),
    ...data.lastEvents,
  ])
}
